/**
 * /compare?ids=a,b,c
 * Side-by-side property comparison — up to 3 properties, driven by
 * comma-separated `ids` query param. Fetches each property in parallel
 * and lays out one column per property with matching spec rows.
 *
 * Access: public (no auth needed) so anyone can share the URL.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, X, Bed, Bath, MapPin, Loader2, Heart } from "lucide-react";

import PageShell from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { getApiUrl } from "@/utils/api";
import { resolveImageUrl } from "@/utils/imageUrl";
import { useFavourites } from "@/hooks/useFavourites";
import { useBackHref } from "@/hooks/useBackHref";

const API = getApiUrl();
const MAX_PROPERTIES = 3;

function money(price, cur = "£") {
  if (price === undefined || price === null || price === "") return "—";
  try { return `${cur}${Number(price).toLocaleString()}`; } catch { return `${cur}${price}`; }
}

// Rows we compare. Kept declarative so it's easy to add new fields later.
const COMPARE_ROWS = [
  { key: "price",              label: "Price",           format: (p) => money(p.price, p.currency || "£") },
  { key: "display_address",    label: "Address",         format: (p) => p.display_address || "—" },
  { key: "postcode",           label: "Postcode",        format: (p) => p.postcode || "—" },
  { key: "beds",               label: "Bedrooms",        format: (p) => p.beds ?? "—" },
  { key: "baths",              label: "Bathrooms",       format: (p) => p.baths ?? "—" },
  { key: "size",               label: "Size",            format: (p) => p.size || "—" },
  { key: "property_type",      label: "Type",            format: (p) => (p.property_type || "—").toString() },
  { key: "tenure",             label: "Tenure",          format: (p) => (p.tenure || "—").toString() },
  { key: "council_tax_band",   label: "Council tax",     format: (p) => p.council_tax_band ? `Band ${p.council_tax_band}` : "—" },
  { key: "parking_spaces",     label: "Parking",         format: (p) => p.parking_spaces ? `${p.parking_spaces} space${p.parking_spaces === 1 ? "" : "s"}` : "—" },
  { key: "garden_facing",      label: "Garden",          format: (p) => p.garden_facing || "—" },
  { key: "nearest_stations",   label: "Nearest stations",format: (p) => p.nearest_stations || "—" },
  { key: "features",           label: "Features",        format: (p) => p.features || "—" },
];

export default function ComparePage() {
  const location = useLocation();
  const { toggle: toggleFav, isFavourite } = useFavourites();

  // Resolve where the "Back" link should point — router state > sessionStorage
  // stash > document.referrer > /properties fallback. Shared with every other
  // page that renders a "Back to …" link so the label + destination stay
  // consistent across the site.
  const back = useBackHref("/properties");
  const backHref = back.href;
  const backLabel = back.label;

  const ids = useMemo(() => {
    const p = new URLSearchParams(location.search || "");
    const raw = (p.get("ids") || "").split(",").map((s) => s.trim()).filter(Boolean);
    return raw.slice(0, MAX_PROPERTIES);
  }, [location.search]);

  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    if (ids.length === 0) {
      setProperties([]); setLoading(false); return;
    }
    setLoading(true);
    Promise.all(
      ids.map((id) =>
        axios.get(`${API}/properties/${id}`)
          .then((r) => r.data)
          .catch(() => null),
      ),
    ).then((results) => {
      if (!alive) return;
      const ok = results.filter(Boolean);
      setProperties(ok);
      if (ok.length === 0) setError("None of the selected properties could be loaded.");
    }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [ids]);

  const removeId = (id) => {
    const next = ids.filter((x) => x !== id);
    // Rewrite URL without a full navigation so the compare recomputes.
    const url = next.length === 0 ? "/my-favourites" : `/compare?ids=${next.join(",")}`;
    window.location.replace(url);
  };

  // A row "differs" when at least one property has a distinct value from the others
  // — used to visually highlight columns where a choice actually matters.
  const rowDiffers = (row) => {
    const seen = new Set(properties.map((p) => JSON.stringify(row.format(p))));
    return seen.size > 1;
  };

  return (
    <PageShell chrome={true}>
      <div data-public-site="true" className="min-h-screen bg-background">
        <div className="max-w-6xl mx-auto px-6 py-10">
          <Link
            to={backHref}
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6"
            data-testid="compare-back-link"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> {backLabel}
          </Link>
          <div className="mb-8">
            <h1 className="text-3xl font-serif mb-1">Compare properties</h1>
            <p className="text-sm text-muted-foreground">
              Up to {MAX_PROPERTIES} properties side by side. Rows where the values differ are highlighted so trade-offs are easy to spot.
            </p>
          </div>

          {loading ? (
            <div className="text-center py-16 text-sm text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" /> Loading…
            </div>
          ) : ids.length === 0 || properties.length === 0 ? (
            <div className="text-center border border-dashed border-border rounded-lg p-12" data-testid="compare-empty">
              <p className="text-muted-foreground mb-4">{error || "No properties selected — pick 2 or 3 from your favourites."}</p>
              <Link to={backHref} className="text-primary hover:underline text-sm font-medium">
                {backLabel} →
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto border border-border rounded-lg" data-testid="compare-table">
              <table className="w-full text-sm">
                <thead>
                  <tr>
                    <th className="p-3 text-left bg-muted/40 border-b border-border w-40">Property</th>
                    {properties.map((p) => {
                      const img = (p.gallery || [])[0]?.media_url || "";
                      const fav = isFavourite(p.property_id);
                      return (
                        <th key={p.property_id} className="p-3 border-b border-l border-border align-top" data-testid={`compare-col-${p.property_id}`}>
                          <div className="relative">
                            <button
                              type="button"
                              onClick={() => removeId(p.property_id)}
                              className="absolute top-1 right-1 p-1 rounded-full bg-white/90 hover:bg-white shadow"
                              title="Remove from comparison"
                              data-testid={`compare-remove-${p.property_id}`}
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                            <Link to={`/property/${p.property_id}`}>
                              <div
                                className="h-32 w-full bg-cover bg-center bg-muted rounded"
                                style={img ? { backgroundImage: `url(${resolveImageUrl(img)})` } : {}}
                              />
                            </Link>
                            <Link to={`/property/${p.property_id}`} className="block mt-2 font-semibold hover:text-primary">
                              {p.title || "Property"}
                            </Link>
                            <div className="text-xs text-muted-foreground flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              <span className="truncate">{p.display_address || "—"}</span>
                            </div>
                            <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
                              {p.beds ? <span className="flex items-center gap-1"><Bed className="h-3 w-3" />{p.beds}</span> : null}
                              {p.baths ? <span className="flex items-center gap-1"><Bath className="h-3 w-3" />{p.baths}</span> : null}
                            </div>
                            <button
                              type="button"
                              onClick={() => toggleFav(p)}
                              className={`mt-2 inline-flex items-center gap-1 text-xs px-2 py-1 rounded border transition-colors ${fav ? "bg-red-50 border-red-200 text-red-700" : "border-border text-muted-foreground hover:text-foreground"}`}
                              data-testid={`compare-fav-${p.property_id}`}
                            >
                              <Heart className={`h-3 w-3 ${fav ? "fill-red-500 text-red-500" : ""}`} />
                              {fav ? "Saved" : "Save"}
                            </button>
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {COMPARE_ROWS.map((row) => {
                    const differs = rowDiffers(row);
                    return (
                      <tr key={row.key} className={differs ? "bg-amber-50/40" : ""} data-testid={`compare-row-${row.key}`}>
                        <td className="p-3 font-medium text-muted-foreground border-t border-border">
                          {row.label}
                          {differs && (
                            <span className="ml-1 text-[10px] uppercase tracking-wide text-amber-700 font-semibold">diff</span>
                          )}
                        </td>
                        {properties.map((p) => (
                          <td key={p.property_id} className="p-3 border-t border-l border-border align-top">
                            <div className="whitespace-pre-wrap break-words">{row.format(p)}</div>
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </PageShell>
  );
}
